<div class="flex flex-col phoenix-chips">
  <div class="w-full text-right flex flex-row items-center justify-end">
    <div class="flex-1 flex flex-row items-center gap-2">
      @if (showTitle) {
        <span class="text-sm font-semibold">{{ title }}</span>
      }
      @if (isLoading) {
        <mat-progress-spinner
          diameter="20"
          mode="indeterminate"
          [strokeWidth]="3"
        ></mat-progress-spinner>
      }
    </div>
    @if (hasNavigation && (control.value?.length ?? 0) > 500) {
      <div
        class="flex flex-row items-center justify-end theme-bg-2 rounded-md px-1"
      >
        <button
          type="button"
          matTooltip="Go to beginning"
          mat-icon-button
          color="primary"
          class="material-icons-outlined"
          [disabled]="navigationValuesIndex === 0"
          [attr.aria-label]="'Go to beginning'"
          (click)="goToBeginning()"
        >
          keyboard_double_arrow_left
        </button>
        <button
          type="button"
          matTooltip="Previous Data"
          mat-icon-button
          color="primary"
          class="material-icons-outlined"
          [disabled]="navigationValuesIndex === 0"
          [attr.aria-label]="'Previous Data'"
          (click)="onPreviousData()"
        >
          chevron_left
        </button>
        <span class="mx-2 text-xs font-medium"
          >Showing {{ getNavigationValuesRange() }} of
          {{ this.control.value?.length ?? 0 }}</span
        >
        <button
          type="button"
          matTooltip="Next Data"
          mat-icon-button
          color="primary"
          class="material-icons-outlined"
          [disabled]="disabledNextButton"
          [attr.aria-label]="'Next Data'"
          (click)="onNextData()"
        >
          chevron_right
        </button>
        <button
          type="button"
          matTooltip="Go to end"
          mat-icon-button
          color="primary"
          class="material-icons-outlined"
          [disabled]="disabledNextButton"
          [attr.aria-label]="'Go to end'"
          (click)="goToEnd()"
        >
          keyboard_double_arrow_right
        </button>
      </div>
    }
    <ng-content></ng-content>
    <button
      type="button"
      matTooltip="Clear the field"
      mat-icon-button
      color="warn"
      [attr.aria-label]="'Clear all ' + title"
      [disabled]="disabled || loadingAutocomplete"
      class="material-icons-outlined ml-2"
      (click)="resetChips()"
    >
      backspace
    </button>
  </div>

  <!-- When not in readonly mode, show input/autocomplete -->
  @if (!readonly && !disabled) {
    @if (!autocompleteConfig) {
      <mat-form-field [floatLabel]="floatLabelStatus" class="w-full">
        <mat-label>{{ title }}</mat-label>
        <input
          matInput
          autocomplete="off"
          [placeholder]="'Add ' + title.toLowerCase()"
          [attr.aria-label]="
            ariaLabel || title.replace(' ', '').toLocaleLowerCase() + 'Chips'
          "
          [errorStateMatcher]="matcher"
          (keydown)="add($event)"
          (keydown.enter)="$event.preventDefault()"
          [formControl]="inputControl"
          [name]="title.replace(' ', '').toLocaleLowerCase()"
          (paste)="paste($event)"
        />

        <!-- Error Messages -->
        @if (inputControl.errors && errorMessages) {
          <mat-error>
            <small>{{ errorMessages[errorKey] }}</small>
          </mat-error>
        } @else if (inputControl.errors) {
          <mat-error>
            <small>{{ inputControl | formError }}</small>
          </mat-error>
        } @else if (control.errors) {
          <mat-error>
            <small>{{ control | formError }}</small>
          </mat-error>
        }

        <!-- Hints -->
        @if (hasNavigation && (control.value?.length ?? 0) >= 499) {
          <mat-hint class="text-xs theme-text-2">
            New values will be added at the end of navigation
          </mat-hint>
        }

        @if (hintTemplate) {
          <mat-hint>
            <ng-container *ngTemplateOutlet="hintTemplate"></ng-container>
          </mat-hint>
        }
      </mat-form-field>
    } @else {
      @if (isServerSideAutocomplete) {
        <phoenix-server-side-autocomplete
          class="flex flex-col"
          #chipsAutocomplete
          [loadingList]="loadingAutocomplete"
          [title]="title"
          [cleanAfterSelect]="true"
          [attrFilter]="serverSideConfig.attrFilter ?? ''"
          [attrToSave]="serverSideConfig.attrToSave ?? ''"
          [customFilter]="serverSideConfig.customFilter ?? undefined"
          [serverCallback]="serverSideConfig.serverCallback"
          [pasteCallback]="serverSideConfig.pasteCallback ?? undefined"
          [formatDataResponse]="
            serverSideConfig.formatDataResponse ?? undefined
          "
          [sortOptions]="serverSideConfig.sortOptions ?? undefined"
          [optionHtml]="serverSideConfig.optionHtml ?? undefined"
          [showClearButton]="serverSideConfig.showClearButton ?? false"
          [fetchOnFocus]="serverSideConfig.fetchOnFocus ?? false"
          [noOptionsMessage]="serverSideConfig.noOptionsMessage ?? ''"
          [disabled]="disabled"
          [hintTemplate]="hintTemplate"
          [parentControl]="control"
          (valueChange)="addAutocomplete($event)"
        ></phoenix-server-side-autocomplete>
      } @else {
        <phoenix-autocomplete
          class="flex flex-col"
          #chipsAutocomplete
          [loadingList]="loadingAutocomplete"
          [title]="title"
          [cleanAfterSelect]="true"
          [hideSingleSelectionIndicator]="true"
          [attrFilter]="normalAutocompleteConfig.attrFilter ?? null"
          [attrToSave]="normalAutocompleteConfig.attrToSave ?? null"
          [customFilter]="normalAutocompleteConfig.customFilter ?? null"
          [freeText]="normalAutocompleteConfig.freeText ?? false"
          [pasteCallback]="normalAutocompleteConfig.pasteCallback ?? undefined"
          [optionHtml]="normalAutocompleteConfig.optionHtml ?? undefined"
          [noOptionsMessage]="normalAutocompleteConfig.noOptionsMessage ?? ''"
          [optionList]="autocompleteList"
          [errorMessages]="errorMessages"
          [floatLabelStatus]="floatLabelStatus"
          [hintTemplate]="hintTemplate"
          [parentControl]="control"
          [disabled]="disabled"
          (valueChange)="addAutocomplete($event)"
        ></phoenix-autocomplete>
      }
    }
  }

  <!-- Always show chips container -->
  <div class="mb-4">
    <ng-container *ngTemplateOutlet="chipsContainer"></ng-container>
  </div>
</div>

<!-- Reusable template for chips container -->
<ng-template #chipsContainer>
  <div
    #chipsContainerElement
    [ngClass]="{
      'border border-red-500 bg-red-50': internalInvalidChipIds.length > 0,
    }"
    class="flex flex-wrap mt-2 p-2 rounded-md theme-bg-2 min-h-[60px] max-h-[160px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent items-start content-start chips-container"
  >
    <ng-template [ngTemplateOutlet]="chips"></ng-template>
  </div>
</ng-template>

<!-- Template for rendering chips -->
<ng-template #chips>
  <mat-chip-set class="w-full flex flex-wrap gap-1">
    @if (hasNavigation) {
      <ng-container
        *ngTemplateOutlet="
          chipList;
          context: { values: navigationValues, useValue: true }
        "
      ></ng-container>
    } @else {
      <ng-container
        *ngTemplateOutlet="
          chipList;
          context: { values: control.value, useValue: false }
        "
      ></ng-container>
    }
    <!-- Empty state message with delayed appearance -->
    @if (
      (hasNavigation
        ? navigationValues.length
        : (control.value?.length ?? 0)) === 0
    ) {
      <div
        class="theme-text-2 text-sm py-2 px-1 italic text-center w-full empty-state"
      >
        No items added
      </div>
    }
  </mat-chip-set>
</ng-template>
<!-- Template for rendering individual chips -->
<ng-template #chipList let-values="values" let-useValue="useValue">
  @for (value of values; track i; let i = $index) {
    <mat-chip
      class="hover:shadow-md chip-item"
      [matTooltip]="chipDisplay(value)"
      [ngClass]="{
        'red-theme-bg-1--chip': internalInvalidChipIds.includes(value),
      }"
      matTooltipPosition="above"
      matTooltipClass="chip-tooltip"
      animate.enter="phoenix-chips__chip-enter"
    >
      @let addDelete =
        !disabled && !control.disabled && !(value | containInArray: filterList);
      <div class="flex items-center justify-between w-full max-w-full">
        @if (chipIconText?.(value)) {
          <span
            class="ml-2 flex-shrink-0 leading-none"
            [ngClass]="chipIconClass"
            [matTooltip]="chipIconTooltip"
            matTooltipPosition="above"
            matTooltipClass="chip-tooltip"
          >
            {{ chipIconText?.(value) }}
          </span>
        }
        <span
          class="whitespace-nowrap overflow-hidden text-ellipsis inline-block px-2"
          [ngClass]="
            addDelete ? 'max-w-[90%]' : 'max-w-full cursor-not-allowed'
          "
        >
          {{ chipDisplay(value) }}
        </span>
        @if (addDelete) {
          <button
            (click)="useValue ? remove(i, value) : remove(i)"
            matChipRemove
            [attr.aria-label]="'Remove ' + chipDisplay(value)"
            class="ml-auto !h-full flex-shrink-0"
          >
            <mat-icon class="text-[18px]">cancel</mat-icon>
          </button>
        }
      </div>
    </mat-chip>
  }
</ng-template>

<!-- Empty state message is now inlined in the chips template -->
